import React, { useState, useEffect, useRef } from 'react';
import logo from './logo_blade.png';
import { auth, db } from './services/firebase';
import { doc, getDoc, getDocs, collection, query, where, onSnapshot, updateDoc, addDoc } from 'firebase/firestore';
import LoginTelefone from './screens/LoginTelefone';
import LoginBarbearia from './screens/LoginBarbearia';
import CadastroBarbearia from './screens/CadastroBarbearia';
import VerificacaoEmail from './screens/VerificacaoEmail';
import TermosModal from './components/TermosModal';
import PerfilCliente from './screens/PerfilCliente';
import Agendamento from './screens/Agendamento';
import Barbeiros from './screens/Barbeiros';
import Horarios from './screens/Horarios';
import Confirmacao from './screens/Confirmacao';
import PainelBarbearia from './screens/PainelBarbearia';
import AdminAprovar from './screens/AdminAprovar';
import AdminDashboard from './screens/AdminDashboard';
import { useSessionPersistence } from './hooks/useSessionPersistence';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function App() {
  // ?tipo=barbearia (vindo da landing /blade) abre direto no cadastro da barbearia
  const [tela, setTela] = useState(() =>
    new URLSearchParams(window.location.search).get('tipo') === 'barbearia'
      ? 'cadastroBarbearia'
      : 'splash'
  );
  const [usuario, setUsuario] = useState(null);
  const [carregandoAuth, setCarregandoAuth] = useState(true);
  const [barbearia, setBarbearia] = useState(null);
  const [barbeiro, setBarbeiro] = useState(null);
  const [dadosAgendamento, setDadosAgendamento] = useState(null);
  const [emailVerificacao, setEmailVerificacao] = useState('');
  // Aceite de termos pendente (clientes antigos via SMS cadastrados sem termosAceitos)
  const [precisaAceitarTermos, setPrecisaAceitarTermos] = useState(false);
  const [mostrarTermosLeitura, setMostrarTermosLeitura] = useState(false);
  const [aceitandoTermos, setAceitandoTermos] = useState(false);
  const [painelAbaInicial, setPainelAbaInicial] = useState('home');
  const [painelCfgTabInicial, setPainelCfgTabInicial] = useState('barbeiros');
  const [proximosAgendamentos, setProximosAgendamentos] = useState([]);
  const [agendamentoParaCancelar, setAgendamentoParaCancelar] = useState(null);
  const [agendamentoParaEditar, setAgendamentoParaEditar] = useState(null);
  const [abrindoEdicao, setAbrindoEdicao] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  // Avaliação
  const [pendenteAvaliacao, setPendenteAvaliacao] = useState(null);
  const [avaliacaoEstrelas, setAvaliacaoEstrelas] = useState(0);
  const [avaliacaoComentario, setAvaliacaoComentario] = useState('');
  const [enviandoAvaliacao, setEnviandoAvaliacao] = useState(false);

  // Deep link: /{slug} → abre direto na seleção de barbeiro da barbearia
  // Slugs válidos: 3-40 chars, letras/números/hífen; ignora /admin e /b/
  const deepLinkId = useRef((() => {
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/b/')) return null;
    if (path === '/blade') return null; // rota reservada da landing (servida estática pela Vercel)
    const m = path.match(/^\/([a-zA-Z0-9][a-zA-Z0-9-]{1,38}[a-zA-Z0-9])$/);
    return m ? m[1] : null;
  })());
  const [deepLinkNome, setDeepLinkNome] = useState(null);

  // Auto-refresh de tokens para manter sessão ativa
  useSessionPersistence();

  useEffect(() => {
    if (window.location.hostname === 'localhost') return;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => {
      OneSignal.init({
        appId: '43e4eb29-0b0a-4c54-b52d-1b7211477030',
        notifyButton: { enable: false },
        welcomeNotification: { disable: true },
      });
    });
  }, []);

  // Cliente logado → torna-se endereçável por push (tag clienteId), espelhando o opt-in
  // que a barbearia faz no PainelBarbearia. Sem isso, /api/notify não tem como mirar o cliente.
  useEffect(() => {
    if (window.location.hostname === 'localhost') return;
    if (!usuario || usuario.tipo !== 'cliente' || !usuario.uid) return;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => {
      OneSignal.Notifications.requestPermission().catch(() => {});
      OneSignal.User.addTag('clienteId', usuario.uid);
      OneSignal.User.PushSubscription.optIn().catch(() => {});
    });
  }, [usuario]);

  // Aceite de termos retroativo: clientes antigos (cadastro por SMS antes do aceite)
  // não têm termosAceitos. Pede o aceite uma vez no próximo acesso.
  useEffect(() => {
    if (!usuario || usuario.tipo !== 'cliente' || !usuario.uid) { setPrecisaAceitarTermos(false); return; }
    let ativo = true;
    getDoc(doc(db, 'clientes', usuario.uid))
      .then(snap => { if (ativo && snap.exists() && !snap.data().termosAceitos) setPrecisaAceitarTermos(true); })
      .catch(() => {});
    return () => { ativo = false; };
  }, [usuario]);

  async function aceitarTermosCliente() {
    if (!usuario?.uid) return;
    setAceitandoTermos(true);
    try {
      await updateDoc(doc(db, 'clientes', usuario.uid), {
        termosAceitos: true,
        termosAceitosEm: new Date().toISOString(),
      });
      setPrecisaAceitarTermos(false);
    } catch (_) {
    } finally {
      setAceitandoTermos(false);
    }
  }

  // Busca nome da barbearia pelo slug para exibir banner no splash
  useEffect(() => {
    if (!deepLinkId.current) return;
    const slug = deepLinkId.current;
    getDocs(query(collection(db, 'barbearias'), where('slug', '==', slug)))
      .then(snaps => { if (!snaps.empty) setDeepLinkNome(snaps.docs[0].data().nome || ''); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca todos agendamentos do cliente — próximos + pendentes de avaliação (tempo real)
  useEffect(() => {
    if (!usuario || usuario.tipo !== 'cliente') {
      setProximosAgendamentos([]);
      setPendenteAvaliacao(null);
      return;
    }

    const q = query(
      collection(db, 'agendamentos'),
      where('clienteId', '==', usuario.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const agora = new Date();
      const todos = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      function dataAgend(a) {
        const mesIndex = MESES.indexOf(a.mes);
        if (mesIndex < 0) return null;
        const dt = new Date(agora.getFullYear(), mesIndex, parseInt(a.dia));
        const [hh, mm] = (a.horario || '00:00').split(':').map(Number);
        dt.setHours(hh, mm, 0, 0);
        return dt;
      }

      // Próximos: futuros e não cancelados/bloqueados
      const proximos = todos
        .filter(a => a.status !== 'cancelado' && a.status !== 'bloqueado')
        .filter(a => { const dt = dataAgend(a); return dt && dt > agora; })
        .sort((a, b) => {
          const dtA = dataAgend(a), dtB = dataAgend(b);
          return (dtA?.getTime() || 0) - (dtB?.getTime() || 0);
        });
      setProximosAgendamentos(proximos);

      // Pendente de avaliação: passados, confirmados, ainda não avaliados
      const pendente = todos
        .filter(a => a.status === 'confirmado' && !a.avaliado)
        .filter(a => { const dt = dataAgend(a); return dt && dt < agora; })
        .sort((a, b) => (b.criadoEm || '').localeCompare(a.criadoEm || ''))[0] || null;
      setPendenteAvaliacao(pendente);
    });

    return () => unsub();
  }, [usuario]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCancelarAgendamento() {
    if (!agendamentoParaCancelar) return;
    setCancelando(true);
    try {
      await updateDoc(doc(db, 'agendamentos', agendamentoParaCancelar.id), { status: 'cancelado' });
    } catch (e) {
      console.error('Erro ao cancelar:', e);
    }
    setCancelando(false);
    setAgendamentoParaCancelar(null);
  }

  // Editar: abre direto a barbearia do agendamento (escolha de barbeiro → horário/serviço),
  // sem precisar redescobrir a barbearia na lista. Mantém o agendamento original em estado
  // para que a Confirmacao cancele o antigo só depois de salvar o novo.
  async function handleEditar(agendamento) {
    if (abrindoEdicao) return;
    setAbrindoEdicao(true);
    try {
      const snap = await getDoc(doc(db, 'barbearias', agendamento.barbeariaId));
      if (snap.exists()) {
        setBarbearia({ id: snap.id, ...snap.data() });
        setAgendamentoParaEditar(agendamento);
        setTela('barbeiros');
      } else {
        // Fallback: barbearia não encontrada → cai no fluxo de descoberta
        setAgendamentoParaEditar(agendamento);
        setTela('agendamento');
      }
    } catch (e) {
      console.error('Erro ao abrir edição:', e);
      setAgendamentoParaEditar(agendamento);
      setTela('agendamento');
    } finally {
      setAbrindoEdicao(false);
    }
  }

  function irParaInicio() {
    setAgendamentoParaEditar(null);
    setTela('splash');
  }

  async function handleEnviarAvaliacao() {
    if (!pendenteAvaliacao || avaliacaoEstrelas === 0) return;
    setEnviandoAvaliacao(true);
    try {
      await addDoc(collection(db, 'avaliacoes'), {
        barbeariaId: pendenteAvaliacao.barbeariaId || '',
        barbeiroNome: pendenteAvaliacao.barbeiro || '',
        clienteId: usuario.uid,
        // nome do próprio cliente (agendamento não guarda mais PII inline)
        clienteNome: usuario.nome || pendenteAvaliacao.clienteNome || '',
        estrelas: avaliacaoEstrelas,
        comentario: avaliacaoComentario.trim(),
        agendamentoId: pendenteAvaliacao.id,
        criadoEm: new Date().toISOString(),
      });
      await updateDoc(doc(db, 'agendamentos', pendenteAvaliacao.id), { avaliado: true });
      setPendenteAvaliacao(null);
      setAvaliacaoEstrelas(0);
      setAvaliacaoComentario('');
    } catch (e) {
      console.error('Erro ao avaliar:', e);
    }
    setEnviandoAvaliacao(false);
  }

  function labelDia(a) {
    const agora = new Date();
    const mesIndex = MESES.indexOf(a.mes);
    if (mesIndex < 0) return `${parseInt(a.dia)} de ${a.mes}`;
    const dt = new Date(agora.getFullYear(), mesIndex, parseInt(a.dia));
    dt.setHours(12, 0, 0, 0);
    const diff = Math.round((dt - new Date().setHours(12, 0, 0, 0)) / 86400000);
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    return `${parseInt(a.dia)} de ${a.mes}`;
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (user.uid === process.env.REACT_APP_ADMIN_UID) {
          window.location.href = '/admin';
          return;
        }
        // Verifica se é barbearia ou cliente no Firestore
        try {
          const barbSnap = await getDoc(doc(db, 'barbearias', user.uid));
          if (barbSnap.exists()) {
            setUsuario({ uid: user.uid, nome: barbSnap.data().nome, tipo: 'barbearia' });
            setPainelAbaInicial('home');
            setPainelCfgTabInicial('barbeiros');
            setTela('painel');
            setCarregandoAuth(false);
            return;
          }
          const userSnap = await getDoc(doc(db, 'clientes', user.uid));
          if (userSnap.exists()) {
            setUsuario({ uid: user.uid, nome: userSnap.data().nome, tipo: 'cliente' });
            // Se veio de um link /{slug}, navega direto para a barbearia
            if (deepLinkId.current) {
              try {
                const qSlug = query(collection(db, 'barbearias'), where('slug', '==', deepLinkId.current));
                const slugSnaps = await getDocs(qSlug);
                if (!slugSnaps.empty) {
                  const s = slugSnaps.docs[0];
                  setBarbearia({ id: s.id, ...s.data() });
                  deepLinkId.current = null;
                  setCarregandoAuth(false);
                  setTela('barbeiros');
                  return;
                }
              } catch (_) {}
              deepLinkId.current = null;
            }
          } else {
            setUsuario({ uid: user.uid, tipo: 'desconhecido' });
          }
        } catch (_) {
          setUsuario({ uid: user.uid });
        }
      } else {
        setUsuario(null);
      }
      setCarregandoAuth(false);
    });
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (carregandoAuth) {
    return (
      <div style={{ backgroundColor: '#111111', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={logo} alt="BLADE" style={{ width: '120px', opacity: 0.5 }} />
      </div>
    );
  }

  async function navegarParaDeepLink() {
    const slug = deepLinkId.current;
    if (!slug) return false;
    try {
      // Busca pela slug customizada da barbearia
      const q = query(collection(db, 'barbearias'), where('slug', '==', slug));
      const snaps = await getDocs(q);
      if (!snaps.empty) {
        const snap = snaps.docs[0];
        deepLinkId.current = null;
        setBarbearia({ id: snap.id, ...snap.data() });
        setTela('barbeiros');
        return true;
      }
    } catch (_) {}
    deepLinkId.current = null;
    return false;
  }

  async function handleLoginCliente(userData, isNew) {
    setUsuario({ ...userData, tipo: 'cliente' });
    if (deepLinkId.current) {
      const ok = await navegarParaDeepLink();
      if (!ok) setTela('agendamento');
    } else if (isNew) {
      setTela('boasVindasCliente');
    } else {
      setTela('agendamento');
    }
  }

  function handleLoginBarbearia(barbeariaData) {
    setUsuario({ uid: barbeariaData.uid, nome: barbeariaData.nome, tipo: 'barbearia' });
    setPainelAbaInicial('home');
    setPainelCfgTabInicial('barbeiros');
    setTela('painel');
  }

  function renderTela() {

    if (tela === 'login') {
      return (
        <LoginTelefone
          tipo="cliente"
          onLogin={handleLoginCliente}
          onVoltar={() => setTela('splash')}
        />
      );
    }

    if (tela === 'loginBarbearia') {
      return (
        <LoginBarbearia
          onLogin={handleLoginBarbearia}
          onCadastro={() => setTela('cadastroBarbearia')}
          onVoltar={() => setTela('splash')}
          onEmailNaoVerificado={(em) => { setEmailVerificacao(em); setTela('verificacaoBarbearia'); }}
        />
      );
    }

    if (tela === 'verificacaoBarbearia') {
      return (
        <VerificacaoEmail
          email={emailVerificacao}
          onVoltar={() => setTela('loginBarbearia')}
        />
      );
    }

    if (tela === 'cadastroBarbearia') {
      return (
        <CadastroBarbearia
          onCadastrar={() => setTela('loginBarbearia')}
          onVoltar={() => setTela('loginBarbearia')}
          onInicio={() => setTela('splash')}
        />
      );
    }

    if (tela === 'boasVindasCliente') {
      return (
        <div style={{
          backgroundColor: '#111111', minHeight: '100vh',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'Arial, sans-serif', padding: '60px 24px 48px', textAlign: 'center'
        }}>
          <img src={logo} alt="BLADE" style={{ width: '140px' }} />

          <div style={{ width: '100%' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>👋</div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#e8e8e8', marginBottom: '10px' }}>
              Olá, {usuario?.nome?.split(' ')[0]}!
            </h2>
            <p style={{ fontSize: '14px', color: '#8f8f8f', lineHeight: '1.7', marginBottom: '36px' }}>
              Complete seu perfil para agendar
            </p>
            <button
              onClick={() => setTela('perfilCliente')}
              style={{
                width: '100%', backgroundColor: '#2d4a2d',
                color: '#7bc47b', border: 'none', borderRadius: '12px',
                padding: '16px', fontSize: '15px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'Arial, sans-serif', marginBottom: '12px'
              }}
            >
              Completar perfil
            </button>
            <button
              onClick={() => setTela('agendamento')}
              style={{
                width: '100%', backgroundColor: 'transparent',
                color: '#7d7d7d', border: '0.5px solid #252525', borderRadius: '12px',
                padding: '14px', fontSize: '14px',
                cursor: 'pointer', fontFamily: 'Arial, sans-serif'
              }}
            >
              Agendar agora
            </button>
          </div>

          <p style={{ fontSize: '11px', color: '#2a2a2a' }}>BLADE — Agendamento de barbearias</p>
        </div>
      );
    }

    if (tela === 'completarBarbearia') {
      return (
        <div style={{
          backgroundColor: '#111111', minHeight: '100vh',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'Arial, sans-serif', padding: '60px 24px 48px', textAlign: 'center'
        }}>
          <img src={logo} alt="BLADE" style={{ width: '140px' }} />

          <div style={{ width: '100%' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✂️</div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#e8e8e8', marginBottom: '10px' }}>
              Bem-vindo, {usuario?.nome?.split(' ')[0]}!
            </h2>
            <p style={{ fontSize: '14px', color: '#8f8f8f', lineHeight: '1.7', marginBottom: '36px' }}>
              Complete o seu cadastro para iniciar o atendimento.
            </p>
            <button
              onClick={() => {
                setPainelAbaInicial('config');
                setPainelCfgTabInicial('barbearia');
                setTela('painel');
              }}
              style={{
                width: '100%', backgroundColor: '#2d4a2d',
                color: '#7bc47b', border: 'none', borderRadius: '12px',
                padding: '16px', fontSize: '15px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'Arial, sans-serif'
              }}
            >
              Configurar minha barbearia
            </button>
          </div>

          <p style={{ fontSize: '11px', color: '#2a2a2a' }}>BLADE — Agendamento de barbearias</p>
        </div>
      );
    }

    if (tela === 'perfilCliente') {
      return (
        <PerfilCliente
          onVoltar={() => setTela('splash')}
          onLogout={() => { setUsuario(null); setTela('splash'); }}
        />
      );
    }

    if (tela === 'agendamento') {
      // Guarda: só clientes autenticados acessam o agendamento
      if (!usuario || usuario.tipo !== 'cliente') {
        setTela('login');
        return null;
      }
      return <Agendamento onVerBarbeiros={(b) => { setBarbearia(b); setTela('barbeiros'); }} onInicio={irParaInicio} />;
    }

    if (tela === 'barbeiros') {
      return (
        <Barbeiros
          barbearia={barbearia}
          editando={agendamentoParaEditar}
          onVoltar={() => { if (agendamentoParaEditar) { irParaInicio(); } else { setTela('agendamento'); } }}
          onEscolher={(b) => { setBarbeiro(b); setTela('horarios'); }}
          onInicio={irParaInicio}
        />
      );
    }

    if (tela === 'horarios') {
      return (
        <Horarios
          barbeiro={barbeiro}
          barbearia={barbearia}
          editando={agendamentoParaEditar}
          onVoltar={() => setTela('barbeiros')}
          onConfirmar={(dados) => { setDadosAgendamento({ ...dados, reagendarId: agendamentoParaEditar?.id }); setTela('confirmacao'); }}
          onInicio={irParaInicio}
        />
      );
    }

    if (tela === 'confirmacao') {
      return (
        <Confirmacao
          dados={dadosAgendamento}
          barbearia={barbearia}
          onInicio={irParaInicio}
          onVoltar={() => { setAgendamentoParaEditar(null); setTela('agendamento'); }}
        />
      );
    }

    if (tela === 'painel') {
      return (
        <PainelBarbearia
          onVoltar={() => setTela('splash')}
          onLogout={() => { setUsuario(null); setTela('splash'); }}
          abaInicial={painelAbaInicial}
          cfgTabInicial={painelCfgTabInicial}
        />
      );
    }

    // Splash
    return (
      <div style={{
        backgroundColor: '#111111', minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'Arial, sans-serif', padding: '60px 24px 48px'
      }}>
        <img src={logo} alt="BLADE" style={{ width: '160px' }} />

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px', width: '100%' }}>

          {/* Cards de próximos agendamentos */}
          {proximosAgendamentos.length > 0 && (
            <div style={{ width: '100%', maxWidth: '280px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {proximosAgendamentos.map((a) => {
                const isPend = a.status === 'pendente';
                return (
                <div key={a.id} style={{
                  backgroundColor: isPend ? '#1f1a0a' : '#141f14',
                  border: `1px solid ${isPend ? '#c9a84c' : '#2d4a2d'}`,
                  borderRadius: '16px', padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px' }}>{isPend ? '⏳' : '✅'}</span>
                    <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px', color: isPend ? '#c9a84c' : '#7bc47b' }}>
                      {isPend ? 'Aguardando confirmação' : 'Agendamento confirmado'}
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#d0d0d0', margin: '0 0 12px', lineHeight: '1.6' }}>
                    <strong style={{ color: '#fff' }}>{labelDia(a)}</strong> às{' '}
                    <strong style={{ color: '#fff' }}>{a.horario}</strong> na{' '}
                    <strong style={{ color: '#fff' }}>{a.barbearia}</strong>
                    {a.barbeiro ? <span style={{ color: '#9c9c9c' }}> · {a.barbeiro}</span> : null}
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setAgendamentoParaCancelar(a)} style={{
                      flex: 1, backgroundColor: '#1f1414', color: '#c47b7b',
                      border: '1px solid #4a2d2d', borderRadius: '10px',
                      padding: '9px 0', fontSize: '12px', fontWeight: '600',
                      cursor: 'pointer', fontFamily: 'Arial, sans-serif'
                    }}>Cancelar</button>
                    {!isPend && (
                      <button onClick={() => handleEditar(a)} disabled={abrindoEdicao} style={{
                        flex: 1, backgroundColor: '#1e3a1e', color: '#7bc47b',
                        border: '1px solid #2d4a2d', borderRadius: '10px',
                        padding: '9px 0', fontSize: '12px', fontWeight: '600',
                        cursor: abrindoEdicao ? 'default' : 'pointer', fontFamily: 'Arial, sans-serif',
                        opacity: abrindoEdicao ? 0.6 : 1
                      }}>{abrindoEdicao ? 'Abrindo...' : 'Editar'}</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Modal de confirmação de cancelamento */}
          {agendamentoParaCancelar && (
            <div style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '24px'
            }}>
              <div style={{
                backgroundColor: '#1a1a1a', borderRadius: '20px',
                padding: '28px 24px', width: '100%', maxWidth: '320px',
                border: '1px solid #2a2a2a', textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', marginBottom: '14px' }}>⚠️</div>
                <h3 style={{ color: '#e8e8e8', fontSize: '17px', fontWeight: '700', margin: '0 0 8px' }}>
                  Cancelar agendamento?
                </h3>
                <p style={{ color: '#9c9c9c', fontSize: '13px', margin: '0 0 24px', lineHeight: '1.6' }}>
                  {labelDia(agendamentoParaCancelar)} às{' '}
                  <strong style={{ color: '#aaa' }}>{agendamentoParaCancelar.horario}</strong> na{' '}
                  <strong style={{ color: '#aaa' }}>{agendamentoParaCancelar.barbearia}</strong> será cancelado.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setAgendamentoParaCancelar(null)} style={{
                    flex: 1, backgroundColor: '#252525', color: '#aeaeae',
                    border: '1px solid #333', borderRadius: '12px',
                    padding: '13px 0', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', fontFamily: 'Arial, sans-serif'
                  }}>Voltar</button>
                  <button onClick={handleCancelarAgendamento} disabled={cancelando} style={{
                    flex: 1, backgroundColor: '#2a1414', color: '#c47b7b',
                    border: '1px solid #4a2d2d', borderRadius: '12px',
                    padding: '13px 0', fontSize: '14px', fontWeight: '600',
                    cursor: cancelando ? 'not-allowed' : 'pointer',
                    fontFamily: 'Arial, sans-serif', opacity: cancelando ? 0.6 : 1
                  }}>{cancelando ? 'Cancelando...' : 'Confirmar'}</button>
                </div>
              </div>
            </div>
          )}

          {/* Banner de deep link — quando cliente chega via link da bio do Instagram */}
          {deepLinkNome && !usuario && (
            <div style={{
              width: '100%', maxWidth: '280px', backgroundColor: '#141f14',
              border: '1px solid #2d4a2d', borderRadius: '16px',
              padding: '16px', textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>✂️</div>
              <div style={{ fontSize: '13px', color: '#7bc47b', fontWeight: '600', marginBottom: '4px' }}>
                Agendar em
              </div>
              <div style={{ fontSize: '15px', color: '#e8e8e8', fontWeight: '700', marginBottom: '12px' }}>
                {deepLinkNome}
              </div>
              <button
                onClick={() => setTela('login')}
                style={{
                  width: '100%', backgroundColor: '#2d4a2d', color: '#7bc47b',
                  border: 'none', borderRadius: '10px', padding: '11px 0',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                Entrar para agendar
              </button>
            </div>
          )}

          <button
            onClick={() => usuario?.tipo === 'cliente' ? setTela('agendamento') : setTela('login')}
            style={{
              background: 'linear-gradient(180deg, #3a3a3a 0%, #1a1a1a 100%)',
              color: '#e8e8e8', border: '1px solid #444', borderRadius: '50px',
              padding: '18px 0', fontSize: '20px', cursor: 'pointer', width: '100%',
              maxWidth: '280px', boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
            }}>
            Agendar
          </button>

          <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
            <div onClick={() => usuario?.tipo === 'cliente' ? setTela('perfilCliente') : setTela('login')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%',
                backgroundColor: '#1a1a1a', border: '1.5px solid #2a2a2a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="26" height="26" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="13" r="8" fill="#3a3a3a"/>
                  <path d="M4 34C4 26 10 22 18 22C26 22 32 26 32 34" fill="#3a3a3a"/>
                </svg>
              </div>
              <span style={{ fontSize: '11px', color: '#8f8f8f' }}>Perfil</span>
            </div>

            <div onClick={() => usuario?.tipo === 'barbearia' ? setTela('painel') : setTela('loginBarbearia')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%',
                backgroundColor: '#1a2a1a', border: '1.5px solid #2d4a2d',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="26" height="26" viewBox="0 0 36 36" fill="none">
                  <rect x="6" y="14" width="24" height="16" rx="2" fill="#2d4a2d"/>
                  <path d="M4 16L18 6L32 16" stroke="#7bc47b" strokeWidth="2" strokeLinecap="round"/>
                  <rect x="14" y="22" width="8" height="8" rx="1" fill="#7bc47b"/>
                </svg>
              </div>
              <span style={{ fontSize: '11px', color: '#3d6b3d' }}>Barbearia</span>
            </div>
          </div>
        </div>

        <p style={{ fontSize: '11px', color: '#2a2a2a', textAlign: 'center' }}>
          BLADE — Agendamento de barbearias
        </p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
      <div style={{
        maxWidth: '480px', margin: '0 auto',
        minHeight: '100vh', backgroundColor: '#111111',
        position: 'relative'
      }}>
        {renderTela()}

        {/* Aceite de termos pendente (clientes antigos) — bloqueante */}
        {precisaAceitarTermos && usuario?.tipo === 'cliente' && (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2500, padding: '24px', fontFamily: 'Arial, sans-serif'
          }}>
            <div style={{
              backgroundColor: '#1a1a1a', borderRadius: '22px',
              padding: '28px 22px', width: '100%', maxWidth: '340px',
              border: '1px solid #2a2a2a', textAlign: 'center'
            }}>
              <div style={{ fontSize: '34px', marginBottom: '12px' }}>📄</div>
              <h3 style={{ color: '#e8e8e8', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
                Termos de Uso
              </h3>
              <p style={{ fontSize: '13px', color: '#aeaeae', lineHeight: '1.6', margin: '0 0 20px' }}>
                Para continuar usando o BLADE, você precisa ler e aceitar nossos{' '}
                <span onClick={() => setMostrarTermosLeitura(true)}
                  style={{ color: '#7bc47b', textDecoration: 'underline', cursor: 'pointer' }}>
                  Termos de Uso
                </span>.
              </p>
              <button
                onClick={aceitarTermosCliente}
                disabled={aceitandoTermos}
                style={{
                  width: '100%', backgroundColor: aceitandoTermos ? '#1a2a1a' : '#2d4a2d',
                  color: '#7bc47b', border: 'none', borderRadius: '12px', padding: '15px',
                  fontSize: '14px', fontWeight: '600', cursor: aceitandoTermos ? 'default' : 'pointer',
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                {aceitandoTermos ? 'Salvando...' : 'Li e aceito os Termos'}
              </button>
            </div>
          </div>
        )}

        {mostrarTermosLeitura && <TermosModal tipo="cliente" onFechar={() => setMostrarTermosLeitura(false)} />}

        {/* Pop-up global de avaliação */}
        {pendenteAvaliacao && (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: '24px', fontFamily: 'Arial, sans-serif'
          }}>
            <div style={{
              backgroundColor: '#1a1a1a', borderRadius: '22px',
              padding: '28px 22px', width: '100%', maxWidth: '340px',
              border: '1px solid #2a2a2a', textAlign: 'center'
            }}>
              <div style={{ fontSize: '34px', marginBottom: '12px' }}>⭐</div>
              <h3 style={{ color: '#e8e8e8', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>
                Como foi o atendimento?
              </h3>
              <p style={{ color: '#9c9c9c', fontSize: '13px', margin: '0 0 20px', lineHeight: '1.6' }}>
                Com <strong style={{ color: '#aaa' }}>{pendenteAvaliacao.barbeiro}</strong> na{' '}
                <strong style={{ color: '#aaa' }}>{pendenteAvaliacao.barbearia}</strong>
              </p>

              {/* Estrelas */}
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '18px' }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <span
                    key={n}
                    onClick={() => setAvaliacaoEstrelas(n)}
                    style={{
                      fontSize: '36px', cursor: 'pointer',
                      color: n <= avaliacaoEstrelas ? '#c9a84c' : '#2a2a2a',
                      transition: 'color 0.15s'
                    }}
                  >★</span>
                ))}
              </div>

              {/* Comentário */}
              <textarea
                value={avaliacaoComentario}
                onChange={e => setAvaliacaoComentario(e.target.value.slice(0, 100))}
                placeholder="Deixe um comentário (opcional)..."
                maxLength={100}
                rows={3}
                style={{
                  width: '100%', padding: '11px 12px', borderRadius: '10px',
                  border: '0.5px solid #2a2a2a', backgroundColor: '#111',
                  color: '#e8e8e8', fontSize: '13px', resize: 'none',
                  boxSizing: 'border-box', outline: 'none',
                  fontFamily: 'Arial, sans-serif', marginBottom: '4px'
                }}
              />
              <div style={{ fontSize: '10px', color: '#7d7d7d', textAlign: 'right', marginBottom: '16px' }}>
                {avaliacaoComentario.length}/100
              </div>

              <button
                onClick={handleEnviarAvaliacao}
                disabled={avaliacaoEstrelas === 0 || enviandoAvaliacao}
                style={{
                  width: '100%', backgroundColor: avaliacaoEstrelas > 0 ? '#2d4a2d' : '#1a1a1a',
                  color: avaliacaoEstrelas > 0 ? '#7bc47b' : '#444',
                  border: 'none', borderRadius: '12px',
                  padding: '14px', fontSize: '14px', fontWeight: '600',
                  cursor: avaliacaoEstrelas > 0 && !enviandoAvaliacao ? 'pointer' : 'not-allowed',
                  fontFamily: 'Arial, sans-serif', marginBottom: '10px',
                  opacity: enviandoAvaliacao ? 0.6 : 1
                }}
              >
                {enviandoAvaliacao ? 'Enviando...' : 'Enviar avaliação'}
              </button>
              <button
                onClick={() => { setPendenteAvaliacao(null); setAvaliacaoEstrelas(0); setAvaliacaoComentario(''); }}
                style={{
                  width: '100%', backgroundColor: 'transparent', color: '#7d7d7d',
                  border: '0.5px solid #252525', borderRadius: '12px',
                  padding: '12px', fontSize: '13px',
                  cursor: 'pointer', fontFamily: 'Arial, sans-serif'
                }}
              >
                Avaliar depois
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AppWrapper() {
  if (window.location.pathname === '/admin/aprovar') {
    return <AdminAprovar />;
  }
  if (window.location.pathname === '/admin') {
    return <AdminDashboard />;
  }
  return <App />;
}
export default AppWrapper;
